import { useEffect, useState, type ReactNode } from "react";

/** Unified filters surface — a bottom sheet on mobile, a right-side drawer on
 *  sm+. Owns only the chrome (backdrop, slide-in, header count, scroll body,
 *  sticky footer); the caller fills the body with ``FilterSection``s and drives
 *  the live filter state directly (no draft state; filters apply as they
 *  change, and the footer just closes the sheet).
 *
 *  Shared by Fresh and Placements — extracted from Fresh's original panel. */
export function FiltersDrawer({
  onClose,
  title = "Filters",
  count,
  resultTotal,
  resultNoun = "result",
  onClear,
  children,
}: {
  onClose: () => void;
  title?: string;
  /** Number of active filter groups — shown as a badge next to the title. */
  count: number;
  /** Live result count for the "Show N results" button. */
  resultTotal: number;
  resultNoun?: string;
  onClear: () => void;
  children: ReactNode;
}) {
  // Body-scroll lock + Escape-to-close while the panel is mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  // Slide-in entrance (translate from off-screen edge → resting). Set on mount.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 ${
          entered ? "opacity-100" : "opacity-0"
        }`}
      />
      {/* Panel: bottom sheet on mobile, right drawer on sm+ */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`absolute flex flex-col bg-ink-950 border-white/10 shadow-2xl
          inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl border-t
          sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:h-full sm:w-[22rem] sm:rounded-t-none sm:rounded-l-2xl sm:border-t-0 sm:border-l
          transition-transform duration-300 ease-out
          ${entered ? "translate-y-0 sm:translate-x-0" : "translate-y-full sm:translate-y-0 sm:translate-x-full"}`}
      >
        {/* Drag handle (mobile affordance) */}
        <div className="sm:hidden pt-2.5 pb-1 flex justify-center flex-shrink-0">
          <span className="h-1 w-10 rounded-full bg-white/15" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-5 pt-3 sm:pt-5 pb-3 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold text-slate-100">{title}</h2>
            {count > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-accent-500/20 text-accent-100 text-[11px] font-bold tabular-nums">
                {count}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-white/5 transition"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 scrollbar-thin overscroll-contain">
          {children}
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 flex items-center gap-2.5 px-5 py-3.5 border-t border-white/10 bg-ink-950/95 pb-[max(0.875rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClear}
            disabled={count === 0}
            className="h-11 px-4 rounded-lg text-[13px] font-medium text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-lg text-[13px] font-semibold bg-accent-600 text-white hover:bg-accent-500 transition tabular-nums"
          >
            Show {resultTotal.toLocaleString()} {resultNoun}
            {resultTotal === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}
